/**
 * Formatting helpers. Use these instead of ad-hoc Intl / toLocaleString
 * calls so numbers render consistently everywhere — always with the `num`
 * class (JetBrains Mono + tabular-nums) applied at the render site.
 */

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})
const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 1,
  notation: 'compact',
})
const count = new Intl.NumberFormat('en-US')

export function fmtUsd(n: number | null | undefined, compact = false): string {
  if (n == null || !Number.isFinite(n)) return '—'
  // Snap sub-cent values to a true zero so Intl.currency doesn't
  // render "-$0.00" when a customer's payments sum to a tiny negative
  // rounding remainder (refund + charge that don't quite cancel).
  const v = Math.abs(n) < 0.005 ? 0 : n
  return compact ? usdCompact.format(v) : usd.format(v)
}

/** Format a crypto-or-stablecoin amount with its ticker. SOL gets up
 *  to 6 fraction digits (the smallest unit we quote in); USDC/USDT
 *  get 2. Unknown currencies get the raw number with the ticker as a
 *  suffix. Never pre-applies a `$` — that misled operators into
 *  thinking SOL amounts were USD. */
export function fmtAmount(
  amount: number | null | undefined,
  currency?: string | null,
): string {
  if (amount == null || !Number.isFinite(amount)) return '—'
  const ccy = (currency ?? 'USDC').toUpperCase()
  const digits = ccy === 'SOL' ? 6 : 2
  const n = amount.toLocaleString('en-US', {
    minimumFractionDigits: ccy === 'SOL' ? 2 : 2,
    maximumFractionDigits: digits,
  })
  return `${n} ${ccy}`
}

export function fmtCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return count.format(n)
}

/** ISO 3166-1 alpha-2 → flag emoji. Returns empty string for bad input. */
export function flagEmoji(code: string | null | undefined): string {
  if (!code || code.length !== 2) return ''
  const up = code.toUpperCase()
  if (up < 'AA' || up > 'ZZ') return ''
  const base = 0x1f1e6 - 'A'.charCodeAt(0)
  return (
    String.fromCodePoint(up.charCodeAt(0) + base) +
    String.fromCodePoint(up.charCodeAt(1) + base)
  )
}

export function fmtPercent(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return `${n.toFixed(digits)}%`
}

const date = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
})
const dateTime = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

export function fmtDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—'
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return date.format(d)
}

export function fmtDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—'
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return dateTime.format(d)
}

/** "2 minutes ago" / "in 3 days" — short relative form. */
export function fmtRelative(iso: string | Date | null | undefined): string {
  if (!iso) return '—'
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const delta = d.getTime() - Date.now()
  const abs = Math.abs(delta)
  const future = delta > 0
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 24 * 3600e3],
    ['month', 30 * 24 * 3600e3],
    ['day', 24 * 3600e3],
    ['hour', 3600e3],
    ['minute', 60e3],
    ['second', 1000],
  ]
  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })
  for (const [u, ms] of units) {
    if (abs >= ms || u === 'second') {
      const v = Math.round(delta / ms)
      return rtf.format(future ? v : v, u)
    }
  }
  return '—'
}

/** Days until (positive) or since (negative). Used by expiring-soon UIs. */
export function daysUntil(iso: string | Date | null | undefined): number | null {
  if (!iso) return null
  const d = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return Math.round((d.getTime() - Date.now()) / (24 * 3600e3))
}

/** Safely truncate an id / hash for display: "3f2a…b9c1". */
export function shortId(s: string | null | undefined, head = 4, tail = 4): string {
  if (!s) return '—'
  if (s.length <= head + tail + 1) return s
  return `${s.slice(0, head)}…${s.slice(-tail)}`
}

export function initialsOf(name?: string | null, email?: string | null): string {
  const src = (name ?? email ?? '?').trim()
  if (!src) return '?'
  const parts = src.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return src.slice(0, 2).toUpperCase()
}
